'use client';

import { createGlobalStyle, StyleSheetManager, ThemeProvider } from '@benzinga/themetron';
import React from 'react';
import { CookiesProvider } from 'react-cookie';
import dynamic from 'next/dynamic';
import { SessionContextProvider } from '@benzinga/session-context';
import { ErrorBoundary } from '@benzinga/core-ui';
import { DEFAULT_LANGUAGE, type LocaleType } from '@benzinga/translate';
import { NotificationManager } from '@benzinga/notification-manager';
import { BzIntercomProvider } from '@benzinga/intercom-ui';

import { createSession, type Session } from '@benzinga/session';
import { getGlobalSession, getSessionEnvironment } from '../../../src/utils/session';
import { INTERCOM_APP_ID } from '../../../src/env.client';
import { RootSchema } from './RootSchema';
import { PageLocaleProvider } from './pageLocale';

import '@benzinga/globalStyles';
import '../../../utils/font-awesome';

const Toaster = dynamic(() => import('react-hot-toast').then(mod => ({ default: mod.Toaster })), { ssr: false });

function WrapProviders({
  children,
  locale,
}: {
  children: React.ReactNode;
  locale?: string;
}): React.ReactElement | null {
  const sessionRef = React.useRef<Session | null>(null);
  if (sessionRef.current === null) {
    sessionRef.current =
      typeof window === 'undefined' ? createSession(getSessionEnvironment()) : getGlobalSession();
  }
  const session = sessionRef.current;

  React.useEffect(() => {
    if (!('Notification' in window) || Notification.permission !== 'granted') {
      return;
    }
    void session
      ?.getManager(NotificationManager)
      .setupServiceWorker({ global_key: window.location.host })
      .catch(() => {
        /* best-effort — a failed bootstrap must not surface to the user */
      });
  }, [session]);
  const pageLocale = (locale ?? DEFAULT_LANGUAGE) as LocaleType;

  return (
    <PageLocaleProvider locale={pageLocale}>
      <GlobalTheme locale={pageLocale} />
      <SessionContextProvider session={session}>
        <ErrorBoundary disableFallback={true} name="global">
          <BzIntercomProvider appId={INTERCOM_APP_ID}>
            <CookiesProvider>
              <StyleSheetManager>
                <ThemeProvider theme="modern">
                  <RootSchema />
                  {children}
                  <div id="app-modal-element"></div>
                  <div id="__next"></div>
                  <Toaster
                    position="top-center"
                    toastOptions={{
                      style: {
                        maxWidth: 'unset',
                        fontSize: 14,
                        fontWeight: 'bold',
                      },
                    }}
                  />
                </ThemeProvider>
              </StyleSheetManager>
            </CookiesProvider>
          </BzIntercomProvider>
        </ErrorBoundary>
      </SessionContextProvider>
    </PageLocaleProvider>
  );
}

export default WrapProviders;
const GlobalTheme = createGlobalStyle<{ locale: string }>`
  ${props =>
    ['ko', 'ja'].includes(props.locale)
      ? `*, h1, h2, h3, h4, h5 {
    font-family: Noto Sans KR, Manrope, Manrope-fallback, sans-serif;
  }`
      : ''}`;
